import { getConfig } from "../constants/config";
import { getTokenIn } from "../helpers/getTokenIn";
import { getTotalAmountIn } from "../helpers/getTotalAmountIn";
import { ethers } from "ethers-v6";
import { symphonyAddress } from "../../common/constants/contract";
import { verifySigner } from "../helpers/verifySigner";
import tokenService from "../../common/tokenService";

// Minimal ERC20 ABI for allowance check
const erc20AllowanceAbi = [
  {
    constant: true,
    inputs: [
      { name: "_owner", type: "address" },
      { name: "_spender", type: "address" },
    ],
    name: "allowance",
    outputs: [{ name: "", type: "uint256" }],
    payable: false,
    stateMutability: "view",
    type: "function",
  },
];

/**
 * Checks if the Symphony contract has sufficient token approval for a swap.
 * Handles both direct token address checks and route-based checks.
 *
 * @param {Object} params - Check parameters
 * @param {(string|Array)} params.variable - Token address or route array
 *   - If string: Token address to check (e.g., "0x123...")
 *   - If Array: Route array from getRoute
 * @param {ethers.Signer} params.signer - Signer for checking allowance
 * @param {(string|ethers.BigNumber)} [params.amount] - Amount to check approval for
 *   Required when variable is a token address
 * @param {boolean} [params.includesNative=false] - Whether the swap includes native token
 * @param {Object} [params.options] - Additional options
 * @param {boolean} [params.options.isRaw=true] - If true, amount is in raw units
 * @param {number} [params.options.chainId=config.chainId] - Chain ID for the check
 *
 * @returns {Promise<boolean>} True if approval is sufficient
 *
 * @throws {Error} If amount or token address is missing
 * @throws {Error} If token is not listed on Symphony (when using formatted amounts)
 * @throws {Error} If approval check fails
 *
 * @example
 * // Example 1: Check approval for a route
 * const route = await getRoute({
 *   tokenIn: "0xe30fedd158a2e3b13e9badaeabafc5516e95e8c7", // WSEI
 *   tokenOut: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48", // USDC
 *   amount: "1.0"
 * });
 * const isApproved = await checkApproval({
 *   variable: route,
 *   signer: provider.getSigner()
 * });
 *
 * @example
 * // Example 2: Check approval for a specific token and amount
 * const isApproved = await checkApproval({
 *   variable: "0xe30fedd158a2e3b13e9badaeabafc5516e95e8c7", // WSEI
 *   signer: provider.getSigner(),
 *   amount: "1000000000000000000", // 1 WSEI in wei
 *   options: { isRaw: true }
 * });
 *
 * @example
 * // Example 3: Check approval with formatted amount
 * const isApproved = await checkApproval({
 *   variable: "0xe30fedd158a2e3b13e9badaeabafc5516e95e8c7", // WSEI
 *   signer: provider.getSigner(),
 *   amount: "1.0", // 1 WSEI
 *   options: { isRaw: false }
 * });
 */
export const checkApproval = async ({
  variable, //tokenAddress or route !required
  signer = undefined, //!required
  amount,
  includesNative = false,
  options = { isRaw: true, chainId: getConfig().chainId },
}) => {
  let amountToCheck;
  let tokenToCheck;

  // Get config for non-token data
  const config = getConfig();
  const {
    nativeAddress,
    wrappedNativeAddress,
    chainId,
    additionalTokens,
    overrideDefaultTokens,
  } = config;

  // Get updated token list with user's configuration applied
  const tokens = await tokenService.getAsyncTokenList(
    chainId,
    additionalTokens,
    overrideDefaultTokens,
    nativeAddress
  );

  if (variable === nativeAddress) return true;
  // If route given as variable
  if (Array.isArray(variable) && variable.length !== 0) {
    amountToCheck = (await getTotalAmountIn(variable)).amountIn;
    tokenToCheck = getTokenIn(variable);
    if (tokenToCheck === wrappedNativeAddress && includesNative) return true;
  }
  // If tokenAddress is given as a variable
  if (
    !Array.isArray(variable) &&
    typeof variable === "string" &&
    variable.startsWith("0x") &&
    variable.length === 42 &&
    variable !== nativeAddress
  ) {
    tokenToCheck = variable.toLowerCase();

    if (options.isRaw) amountToCheck = amount;
    if (!options.isRaw) {
      if (!tokens[tokenToCheck])
        throw new Error(
          `Token ${variable} data or decimals missing. This might mean token is not listed on Symphony.`
        );
      amountToCheck = ethers.parseUnits(
        amount,
        tokens[tokenToCheck].attributes.decimals
      );
    }
  }

  if (!amountToCheck || !tokenToCheck)
    throw new Error(
      `Amount or token address is missing to check approval. Check params passed to checkApproval. amountToCheck: ${amountToCheck},tokenToCheck: ${tokenToCheck}`
    );

  try {
    const verifiedSigner = await verifySigner(signer);

    // Create contract instance
    const contract = new ethers.Contract(
      tokenToCheck,
      erc20AllowanceAbi,
      verifiedSigner
    );

    // Get allowance
    const allowance = await contract.allowance(
      await verifiedSigner.getAddress(),
      symphonyAddress
    );

    const isTokenApproved = allowance >= amountToCheck;
    return isTokenApproved;
  } catch (error) {
    console.error("Approval check failed:", error.message);
    throw new Error("Approve check failed.");
  }
};
